import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Copy,
  Check,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import { cn } from '@/utils'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui'
import { useFloatingWindow, useIsSmallScreen } from '@/hooks/useFloatingWindow'
import type { ResizeEdge } from '@/hooks/useFloatingWindow'
import { useProxiedStorageUrl } from '@/hooks/useProxiedStorageUrl'
import { getMediaTypeStyle } from './mediaTypeStyles'
import { cleanPassageText } from './cleanPassageText'
import { getExternalUrl } from './externalUrl'
import { isPdfSource } from './isPdfSource'
import {
  DocumentViewer,
  VideoViewer,
  AudioViewer,
  ScormViewer,
  WebViewer,
  ImageViewer,
} from './viewers'
import type { DisplaySource } from '@/types/source'

interface ExperienceSourceViewerProps {
  source: DisplaySource | null
  sources: DisplaySource[]
  isOpen: boolean
  onClose: () => void
  onNavigate: (direction: 'prev' | 'next') => void
  hasPrev: boolean
  hasNext: boolean
  ragServerUrl?: string
  /** RAG API key for fetching auth-gated proxy storage URLs (same
   *  credential the chat connector sends as x-api-key). */
  ragApiKey?: string
  /** When provided, expired media previews offer a Refresh action that
   *  re-resolves the storage URL (SAS) via useSourceUrl().refreshUrl. */
  onRefreshUrl?: (source: DisplaySource) => Promise<DisplaySource | null>
}

const RESIZE_EDGES: ResizeEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']

// Invisible hit zones along each edge/corner; corners win via higher z.
// touch-none: without it, touch devices can claim these as pan/scroll
// gestures and fire pointercancel instead of pointerup mid-resize.
const RESIZE_HANDLE_CLASSES: Record<ResizeEdge, string> = {
  n: 'absolute top-0 left-2 right-2 h-1.5 cursor-ns-resize touch-none',
  s: 'absolute bottom-0 left-2 right-2 h-1.5 cursor-ns-resize touch-none',
  e: 'absolute right-0 top-2 bottom-2 w-1.5 cursor-ew-resize touch-none',
  w: 'absolute left-0 top-2 bottom-2 w-1.5 cursor-ew-resize touch-none',
  ne: 'absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-10 touch-none',
  nw: 'absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-10 touch-none',
  se: 'absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-10 touch-none',
  sw: 'absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-10 touch-none',
}

const ACTION_CLASS =
  'p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors inline-flex'

function HeaderAction({
  label,
  onClick,
  href,
  children,
}: {
  label: string
  onClick?: () => void
  href?: string
  children: React.ReactNode
}) {
  const trigger = href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={ACTION_CLASS}
    >
      {children}
    </a>
  ) : (
    <button type="button" onClick={onClick} aria-label={label} className={ACTION_CLASS}>
      {children}
    </button>
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  )
}

export function ExperienceSourceViewer(props: ExperienceSourceViewerProps) {
  return createPortal(
    <AnimatePresence>
      {props.isOpen && props.source ? (
        <SourceWindow key="source-window" {...props} source={props.source} />
      ) : null}
    </AnimatePresence>,
    document.body
  )
}

type SourceWindowProps = ExperienceSourceViewerProps & { source: DisplaySource }

function SourceWindow({
  source,
  sources,
  onClose,
  onNavigate,
  hasPrev,
  hasNext,
  ragServerUrl,
  ragApiKey,
  onRefreshUrl,
}: SourceWindowProps) {
  const isSmall = useIsSmallScreen()
  const fw = useFloatingWindow({
    defaultWidth: 860,
    defaultHeight: Math.round(window.innerHeight * 0.75),
    minWidth: 480,
    minHeight: 360,
    storageKey: 'experience-source-viewer-bounds',
    enabled: !isSmall,
  })
  const floating = !isSmall && !fw.isMaximized

  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    rootRef.current?.focus()
  }, [])

  const [copied, setCopied] = useState(false)
  const [refreshedSource, setRefreshedSource] = useState<DisplaySource | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const displaySource = refreshedSource ?? source
  const isPdf = isPdfSource(displaySource)
  const embed = useProxiedStorageUrl(displaySource, {
    ragServerUrl,
    ragApiKey,
    mimeType: isPdf ? 'application/pdf' : undefined,
    enabled: !displaySource.isExpired && (displaySource.mediaType === 'image' || isPdf),
  })
  const [scormEntryUrl, setScormEntryUrl] = useState<string | null>(null)
  const externalUrl = getExternalUrl(displaySource, {
    embedUrl: embed.status === 'ready' ? embed.url : null,
    scormEntryUrl,
  })

  // Reset transient state when navigating to a different source
  useEffect(() => {
    setCopied(false)
    setRefreshedSource(null)
    setIsRefreshing(false)
    setScormEntryUrl(null)
  }, [source.id])

  const handleCopyText = useCallback(async () => {
    await navigator.clipboard.writeText(cleanPassageText(source.document))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [source])

  const handleRefresh = useCallback(async () => {
    if (!onRefreshUrl) return
    setIsRefreshing(true)
    try {
      const refreshed = await onRefreshUrl(source)
      if (refreshed) setRefreshedSource(refreshed)
    } finally {
      setIsRefreshing(false)
    }
  }, [source, onRefreshUrl])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    const target = e.target as HTMLElement
    if (target.closest('input, textarea, [contenteditable="true"]')) return
    if (e.key === 'ArrowLeft' && hasPrev) {
      e.preventDefault()
      onNavigate('prev')
    }
    if (e.key === 'ArrowRight' && hasNext) {
      e.preventDefault()
      onNavigate('next')
    }
  }

  const style = getMediaTypeStyle(source.mediaType)
  const currentIndex = sources.findIndex((s) => s.id === source.id)
  const gestureActive = fw.isDragging || fw.isResizing

  function renderViewer() {
    switch (displaySource.mediaType) {
      case 'video':
        return (
          <VideoViewer
            source={displaySource}
            onRefresh={onRefreshUrl ? handleRefresh : undefined}
            isRefreshing={isRefreshing}
          />
        )
      case 'audio':
        return (
          <AudioViewer
            source={displaySource}
            onRefresh={onRefreshUrl ? handleRefresh : undefined}
            isRefreshing={isRefreshing}
          />
        )
      case 'scorm':
        return (
          <ScormViewer
            source={displaySource}
            ragServerUrl={ragServerUrl}
            onEntryUrl={setScormEntryUrl}
          />
        )
      case 'image':
        return (
          <ImageViewer
            // Remount per source: resets the viewer's internal state (e.g.
            // image load status) so it doesn't carry over between sources.
            key={displaySource.id}
            source={displaySource}
            embedUrl={embed.url}
            embedStatus={embed.status}
            onRefresh={onRefreshUrl ? handleRefresh : undefined}
            isRefreshing={isRefreshing}
          />
        )
      case 'web':
        return <WebViewer source={displaySource} />
      default:
        return (
          <DocumentViewer
            key={displaySource.id}
            source={displaySource}
            embedUrl={embed.url}
            embedStatus={embed.status}
          />
        )
    }
  }

  return (
    <motion.div
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-label={source.title}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.15 }}
      className={cn(
        'fixed z-50 flex flex-col overflow-hidden bg-card border border-border shadow-lg outline-none',
        isSmall ? 'inset-x-2 inset-y-3 rounded-xl' : 'rounded-lg',
        !isSmall && fw.isMaximized && 'inset-4'
      )}
      style={
        floating
          ? {
              left: fw.bounds.x,
              top: fw.bounds.y,
              width: fw.bounds.width,
              height: fw.bounds.height,
            }
          : undefined
      }
    >
      {/* Full-viewport shield while dragging/resizing so embedded iframes
          don't swallow pointermove (document-level listeners keep firing).
          Safe inside the motion root: transforms are only active during the
          150ms open/close animation, never during a gesture. */}
      {gestureActive && <div className="fixed inset-0 z-50" />}

      {/* Title bar */}
      <div
        data-testid="source-window-titlebar"
        {...(isSmall
          ? {}
          : fw.isMaximized
            ? { onDoubleClick: fw.titleBarProps.onDoubleClick }
            : fw.titleBarProps)}
        className={cn(
          'flex items-center gap-3 pl-4 pr-2 py-2.5 border-b border-border select-none shrink-0',
          floating && (fw.isDragging ? 'cursor-grabbing' : 'cursor-grab'),
          floating && 'touch-none'
        )}
      >
        <div
          className={cn(
            'px-2.5 py-1 rounded-md text-xs font-medium shrink-0',
            style.btnBg,
            style.btnText
          )}
        >
          {style.label}
        </div>
        <h3
          className="text-sm font-semibold text-foreground truncate min-w-0"
          title={source.title}
        >
          {source.title}
        </h3>
        {sources.length > 1 && (
          <span className="text-xs text-muted-foreground shrink-0">
            {currentIndex + 1} of {sources.length}
          </span>
        )}
        <TooltipProvider delayDuration={300}>
          <div className="ml-auto flex items-center gap-0.5">
            <HeaderAction
              label={copied ? 'Copied' : 'Copy text'}
              onClick={handleCopyText}
            >
              {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
            </HeaderAction>
            {externalUrl && (
              <HeaderAction label="Open externally" href={externalUrl}>
                <ExternalLink className="w-4 h-4" />
              </HeaderAction>
            )}
            {!isSmall && (
              <HeaderAction
                label={fw.isMaximized ? 'Restore' : 'Maximize'}
                onClick={fw.toggleMaximize}
              >
                {fw.isMaximized ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </HeaderAction>
            )}
            <HeaderAction label="Close" onClick={onClose}>
              <X className="w-4 h-4" />
            </HeaderAction>
          </div>
        </TooltipProvider>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto p-5">{renderViewer()}</div>

      {/* Navigation */}
      {sources.length > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-border shrink-0">
          <button
            type="button"
            onClick={() => onNavigate('prev')}
            disabled={!hasPrev}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <button
            type="button"
            onClick={() => onNavigate('next')}
            disabled={!hasNext}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Resize handles + corner grip */}
      {floating && (
        <>
          {RESIZE_EDGES.map((edge) => (
            <div
              key={edge}
              data-resize-edge={edge}
              {...fw.getResizeHandleProps(edge)}
              className={RESIZE_HANDLE_CLASSES[edge]}
            />
          ))}
          <svg
            className="absolute bottom-1 right-1 w-3 h-3 text-muted-foreground/50 pointer-events-none"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M11 5L5 11M11 9L9 11" />
          </svg>
        </>
      )}
    </motion.div>
  )
}
